import { ColorStyleProps, Text } from '@quality24/design-system';
import {
  Admission,
  Patient,
  ReleasedSurvey,
  SurveyPage,
  SurveyPageDataScale,
} from '@quality24/inpatient-typings';
import classNames from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import SurveyNavigation from '@/inpatient-patient-pwa/molecules/SurveyNavigation';
import { useSurvey } from '@/inpatient-patient-pwa/hooks/useSurvey';
import ConfirmationModal from '../ConfirmationModal';
import DefaultQuestionPage from './components/DefaultQuestionPage';
import IntroductionPage from './components/IntroductionPage';
import SmileyQuestionPage from './components/SmileyQuestionPage';
import StaticPage from './components/StaticPage';
import YesOrNoQuestionPage from './components/YesOrNoQuestionPage';

import styles from './DailySurvey.module.scss';
import ScaleQuestionPage from './components/ScaleQuestionPage';

type ComponentSurveyPage = Required<
  Pick<
    SurveyPage,
    'id' | 'category' | 'type' | 'titleI18N' | 'required' | 'descriptionI18N'
  >
> &
  Pick<SurveyPage, 'data'>;

interface ComposedReleasedSurvey
  extends Required<Pick<ReleasedSurvey, 'id' | 'createdAt'>> {
  admission: Pick<Admission, 'id'> & {
    patient: Pick<Patient, 'id' | 'name'>;
  };
  pages: Array<ComponentSurveyPage>;
}

export interface Props {
  className?: string;
  survey: ComposedReleasedSurvey;
}

const DailySurvey: React.FunctionComponent<Props> = ({ className, survey }) => {
  const { t } = useTranslation();
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const navigate = useNavigate();

  const handleSubmitOrRefuse = React.useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  const { currentPage, answer, nav } = useSurvey(
    survey,
    handleSubmitOrRefuse,
    handleSubmitOrRefuse,
  );

  const navigation = {
    ...nav,
    submit: () => setShowConfirmation(true),
  };
  const emptySurvey = nav.answersAmount === 0;

  const isQuestionPage =
    (currentPage.category === 'STATIC_PAGE' &&
      currentPage.type !== 'PAGE_INTRODUCTION') ||
    currentPage.category === 'SINGLE_QUESTION';

  return (
    <div className={classNames(styles.wrapper, className)}>
      {/* Introduction */}
      {currentPage.category === 'STATIC_PAGE' &&
        currentPage.type === 'PAGE_INTRODUCTION' && (
          <IntroductionPage
            page={currentPage}
            patient={survey.admission.patient}
            onAnswer={nav.next}
            onRefuse={nav.refuse}
            onSkip={handleSubmitOrRefuse}
          />
        )}

      {/* Static pages */}
      {currentPage.category === 'STATIC_PAGE' &&
        currentPage.type !== 'PAGE_INTRODUCTION' && (
          <StaticPage page={currentPage} />
        )}

      {/* Multiple choice page */}
      {currentPage.category === 'SINGLE_QUESTION' &&
        currentPage.type === 'OPEN_0_10' && (
          <DefaultQuestionPage page={currentPage} answer={answer} />
        )}

      {/* 5 point smiley face page */}
      {currentPage.category === 'SINGLE_QUESTION' &&
        currentPage.type === '_5_POINT_SMILEY_FACE' && (
          <SmileyQuestionPage page={currentPage} answer={answer} />
        )}

      {/* Yes or no page */}
      {currentPage.category === 'SINGLE_QUESTION' &&
        currentPage.type === 'YES_NO' && (
          <YesOrNoQuestionPage page={currentPage} answer={answer} />
        )}

      {/* Scale page */}
      {currentPage.category === 'SINGLE_QUESTION' &&
        currentPage.type === 'SCALE' && (
          <ScaleQuestionPage
            page={{
              ...currentPage,
              data: currentPage.data as SurveyPageDataScale<ColorStyleProps>,
            }}
            answer={answer}
          />
        )}

      {/* Survey navigation */}
      {isQuestionPage && <SurveyNavigation navigation={navigation} />}

      {/* Submit confirmation modal */}
      <ConfirmationModal
        show={showConfirmation}
        title={
          nav.answersAmount > 0
            ? t('dailySurvey.confirmFinishTitle')
            : t('dailySurvey.noAnswersTitle')
        }
        onHide={() => setShowConfirmation(false)}
        onConfirm={() => (emptySurvey ? nav.refuse() : nav.submit())}
        confirmLabel={
          emptySurvey ? t('dailySurvey.cancelEvaluation') : t('common.send')
        }
        cancelLabel={
          emptySurvey ? t('dailySurvey.reviewQuestions') : t('common.cancel')
        }
        confirmBtnClassName={emptySurvey ? 'btn btn-danger' : ''}
      >
        {nav.answersAmount > 0 ? (
          <Text>{t('dailySurvey.confirmFinishBody')}</Text>
        ) : (
          <Text>{t('dailySurvey.noAnswersBody')}</Text>
        )}
      </ConfirmationModal>
    </div>
  );
};

export default DailySurvey;
